import React, { useEffect, useMemo, useState } from "react";
import AppShell from "@/components/AppShell";
import { useLocale } from "@/contexts/LocaleContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  BarChart3, Calendar, Clock, FileDown, FileText, Flame, MapPin, TrendingUp, UserX,
} from "lucide-react";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function todayISO() { return new Date().toISOString().slice(0, 10); }
function weekAgoISO() {
  const d = new Date(); d.setDate(d.getDate() - 30);
  return d.toISOString().slice(0, 10);
}

export default function Reports() {
  const { formatDateTime } = useLocale();
  const [from, setFrom] = useState(weekAgoISO());
  const [to, setTo] = useState(todayISO());
  const [siteId, setSiteId] = useState("");
  const [granularity, setGranularity] = useState("day");
  const [sites, setSites] = useState([]);
  const [counts, setCounts] = useState(null);
  const [breakSite, setBreakSite] = useState(null);
  const [breakType, setBreakType] = useState(null);
  const [breakHost, setBreakHost] = useState(null);
  const [peaks, setPeaks] = useState(null);
  const [duration, setDuration] = useState(null);
  const [includeAuto, setIncludeAuto] = useState(false);
  const [noShows, setNoShows] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/sites").then((r) => setSites(r.data || [])).catch(() => {}); }, []);

  const params = useMemo(() => {
    const fromIso = `${from}T00:00:00+00:00`;
    const toIso = `${to}T23:59:59+00:00`;
    const p = { from: fromIso, to: toIso };
    if (siteId) p.site_id = siteId;
    return p;
  }, [from, to, siteId]);

  const load = async () => {
    setLoading(true);
    try {
      const [c, bs, bt, bh, pk, dur, ns] = await Promise.all([
        api.get("/reports/visitor-counts", { params: { ...params, granularity } }),
        api.get("/reports/breakdown", { params: { ...params, dimension: "site" } }),
        api.get("/reports/breakdown", { params: { ...params, dimension: "type" } }),
        api.get("/reports/breakdown", { params: { ...params, dimension: "host" } }),
        api.get("/reports/peak-times", { params }),
        api.get("/reports/visit-duration", { params: { ...params, group_by: "type", include_auto: includeAuto } }),
        api.get("/reports/no-shows", { params: { ...params, limit: 50 } }),
      ]);
      setCounts(c.data);
      setBreakSite(bs.data);
      setBreakType(bt.data);
      setBreakHost(bh.data);
      setPeaks(pk.data);
      setDuration(dur.data);
      setNoShows(ns.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [from, to, siteId, granularity, includeAuto]);

  const downloadCsv = (type) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/reports/export.csv?type=${type}&from=${encodeURIComponent(params.from)}&to=${encodeURIComponent(params.to)}${siteId ? `&site_id=${siteId}` : ""}`;
    // Use fetch with auth header so we get CSV download
    fetch(url, { headers: { Authorization: `Bearer ${localStorage.getItem("eb_token") || ""}` } })
      .then(async (r) => {
        if (!r.ok) {
          const text = await r.text();
          throw new Error(text || `HTTP ${r.status}`);
        }
        const blob = await r.blob();
        const u = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = u; a.download = `entrybook-${type}.csv`; a.click();
        URL.revokeObjectURL(u);
      })
      .catch((e) => toast.error(`Export failed: ${formatApiError(e)}`));
  };

  const downloadPdf = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/reports/summary.pdf?from=${encodeURIComponent(params.from)}&to=${encodeURIComponent(params.to)}${siteId ? `&site_id=${siteId}` : ""}`;
    fetch(url, { headers: { Authorization: `Bearer ${localStorage.getItem("eb_token") || ""}` } })
      .then(async (r) => {
        const blob = await r.blob();
        const u = URL.createObjectURL(blob);
        window.open(u, "_blank");
      })
      .catch((e) => toast.error(`PDF failed: ${formatApiError(e)}`));
  };

  return (
    <AppShell>
      <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
        <div>
          <div className="eb-overline mb-2">Module 12 · Reports & Exports</div>
          <h1 className="text-3xl font-semibold flex items-center gap-3" data-testid="reports-heading">
            <BarChart3 className="w-7 h-7 text-orange-500" />
            Reports
          </h1>
          <p className="text-sm text-navy-500 mt-1">Visitor counts, peak times, durations, no-shows. CSV and PDF exports.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => downloadCsv("visits")} className="btn-outline text-xs" data-testid="export-visits-csv">
            <FileDown className="w-3.5 h-3.5" /> Visits CSV
          </button>
          <button onClick={() => downloadCsv("pre_regs")} className="btn-outline text-xs" data-testid="export-pre-regs-csv">
            <FileDown className="w-3.5 h-3.5" /> Pre-regs CSV
          </button>
          <button onClick={() => downloadCsv("no_shows")} className="btn-outline text-xs" data-testid="export-no-shows-csv">
            <FileDown className="w-3.5 h-3.5" /> No-shows CSV
          </button>
          <button onClick={downloadPdf} className="btn-accent text-xs" data-testid="export-summary-pdf">
            <FileText className="w-3.5 h-3.5" /> Summary PDF
          </button>
        </div>
      </div>

      {/* Filter strip */}
      <div className="eb-card p-4 mb-5 flex items-end gap-3 flex-wrap">
        <div>
          <label className="eb-label">From</label>
          <input type="date" className="eb-input" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="rep-from" />
        </div>
        <div>
          <label className="eb-label">To</label>
          <input type="date" className="eb-input" value={to} onChange={(e) => setTo(e.target.value)} data-testid="rep-to" />
        </div>
        <div>
          <label className="eb-label">Site</label>
          <select className="eb-input" value={siteId} onChange={(e) => setSiteId(e.target.value)} data-testid="rep-site">
            <option value="">All sites</option>
            {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <div>
          <label className="eb-label">Granularity</label>
          <select className="eb-input" value={granularity} onChange={(e) => setGranularity(e.target.value)} data-testid="rep-granularity">
            <option value="day">Day</option>
            <option value="week">Week</option>
            <option value="month">Month</option>
          </select>
        </div>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <KpiTile icon={TrendingUp} label="Total visits" value={counts?.total ?? "—"} />
        <KpiTile icon={Clock} label="Avg duration" value={duration?.items?.[0]?.avg_minutes ? `${duration.items[0].avg_minutes} min` : "—"} accent="text-orange-600" />
        <KpiTile icon={Flame} label="Peak day" value={peaks?.peak?.dow != null ? DAYS[peaks.peak.dow] : "—"} accent="text-navy-900" />
        <KpiTile icon={UserX} label="No-shows" value={noShows?.count ?? "—"} accent="text-danger" />
      </div>

      {/* Counts time series */}
      <div className="eb-card p-5 mb-5">
        <h3 className="font-semibold mb-3 inline-flex items-center gap-2">
          <Calendar className="w-4 h-4 text-navy-500" /> Visitor counts ({granularity})
        </h3>
        <BarChart data={counts?.items || []} dataKey="count" xKey="bucket" />
      </div>

      {/* Heatmap */}
      <div className="eb-card p-5 mb-5">
        <h3 className="font-semibold mb-3 inline-flex items-center gap-2">
          <Flame className="w-4 h-4 text-orange-500" /> Peak times — sign-ins by day & hour
        </h3>
        <Heatmap grid={peaks?.grid || Array.from({ length: 7 }, () => Array(24).fill(0))} />
      </div>

      {/* Breakdowns */}
      <div className="grid md:grid-cols-3 gap-4 mb-5">
        <BreakdownCard title="By site" icon={MapPin} data={breakSite} />
        <BreakdownCard title="By visitor type" icon={FileText} data={breakType} />
        <BreakdownCard title="By host" icon={UserX} data={breakHost} />
      </div>

      {/* Duration controls */}
      <div className="eb-card p-5 mb-5">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h3 className="font-semibold inline-flex items-center gap-2"><Clock className="w-4 h-4 text-navy-500" /> Average duration by type</h3>
          <label className="text-xs text-navy-500 inline-flex items-center gap-2">
            <input type="checkbox" checked={includeAuto} onChange={(e) => setIncludeAuto(e.target.checked)}
              data-testid="rep-include-auto" />
            Include auto-sign-outs (skews higher)
          </label>
        </div>
        <table className="w-full text-sm">
          <thead className="text-navy-500 text-xs uppercase tracking-wider">
            <tr><th className="text-left py-2">Type</th><th className="text-right py-2">Avg</th><th className="text-right py-2">Min</th><th className="text-right py-2">Max</th><th className="text-right py-2">Count</th></tr>
          </thead>
          <tbody>
            {(duration?.items || []).map((r) => (
              <tr key={r._id || "_"} className="border-t border-navy-100">
                <td className="py-2 font-medium">{r.label}</td>
                <td className="py-2 text-right">{r.avg_minutes} m</td>
                <td className="py-2 text-right text-navy-400">{r.min_minutes} m</td>
                <td className="py-2 text-right text-navy-400">{r.max_minutes} m</td>
                <td className="py-2 text-right">{r.count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* No-shows */}
      <div className="eb-card p-5">
        <h3 className="font-semibold mb-3 inline-flex items-center gap-2"><UserX className="w-4 h-4 text-danger" /> No-shows ({noShows?.count ?? "—"})</h3>
        {noShows?.items?.length === 0 ? (
          <p className="text-sm text-navy-500">No no-shows in this window — well done.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-navy-500 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left py-2">Visitor</th>
                <th className="text-left py-2">Expected</th>
                <th className="text-left py-2">Site</th>
                <th className="text-left py-2">Host</th>
              </tr>
            </thead>
            <tbody>
              {(noShows?.items || []).map((r) => (
                <tr key={r.id} className="border-t border-navy-100">
                  <td className="py-2 font-medium">{r.visitor_name}</td>
                  <td className="py-2">{r.expected_at ? formatDateTime(r.expected_at) : "—"}</td>
                  <td className="py-2">{r.site_name || "—"}</td>
                  <td className="py-2">{r.host_name || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </AppShell>
  );
}

function KpiTile({ icon: Icon, label, value, accent }) {
  return (
    <div className="eb-card p-4 flex items-start gap-3">
      <Icon className="w-5 h-5 text-navy-400 mt-1" />
      <div>
        <div className="text-[10px] uppercase tracking-wider text-navy-400 font-bold">{label}</div>
        <div className={`text-2xl font-bold ${accent || "text-navy-900"}`}>{value}</div>
      </div>
    </div>
  );
}

function BarChart({ data, dataKey, xKey }) {
  if (!data || data.length === 0) {
    return <div className="text-sm text-navy-400 py-6 text-center">No data in range.</div>;
  }
  const max = Math.max(...data.map((d) => d[dataKey])) || 1;
  return (
    <div className="flex items-end gap-1 h-40 overflow-x-auto" data-testid="rep-bar-chart">
      {data.map((d, i) => {
        const h = Math.max(2, Math.round((d[dataKey] / max) * 100));
        return (
          <div key={i} className="flex flex-col items-center justify-end flex-shrink-0 w-8 group">
            <div className="text-[9px] text-navy-400 mb-1 opacity-0 group-hover:opacity-100">{d[dataKey]}</div>
            <div className="w-6 rounded-t bg-orange-400 hover:bg-orange-500 transition-colors"
              style={{ height: `${h}%` }} title={`${d[xKey]}: ${d[dataKey]}`} />
            <div className="text-[8px] text-navy-400 mt-1 truncate w-8 text-center">{(d[xKey] || "").slice(5)}</div>
          </div>
        );
      })}
    </div>
  );
}

function Heatmap({ grid }) {
  const flat = grid.flat();
  const max = Math.max(...flat, 1);
  return (
    <div className="overflow-x-auto" data-testid="rep-heatmap">
      <div className="grid grid-cols-[40px_repeat(24,1fr)] gap-0.5 min-w-[700px]">
        <div></div>
        {Array.from({ length: 24 }).map((_, h) => (
          <div key={h} className="text-[9px] text-navy-400 text-center">{h % 3 === 0 ? h : ""}</div>
        ))}
        {grid.map((row, d) => (
          <React.Fragment key={d}>
            <div className="text-[10px] text-navy-500 font-medium pr-1 text-right pt-0.5">{DAYS[d]}</div>
            {row.map((v, h) => {
              const intensity = v / max;
              const bg = intensity === 0
                ? "rgba(0,0,0,0.04)"
                : `rgba(255, 107, 53, ${0.18 + 0.78 * intensity})`;
              return (
                <div key={h} title={`${DAYS[d]} ${h}:00 — ${v} visit${v === 1 ? "" : "s"}`}
                  className="h-5 rounded-sm" style={{ background: bg }} />
              );
            })}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

function BreakdownCard({ title, icon: Icon, data }) {
  const items = data?.items?.slice(0, 8) || [];
  const max = Math.max(...items.map((i) => i.count), 1);
  return (
    <div className="eb-card p-5">
      <h3 className="font-semibold mb-3 inline-flex items-center gap-2 text-sm">
        <Icon className="w-4 h-4 text-navy-500" /> {title}
      </h3>
      {items.length === 0 ? (
        <p className="text-xs text-navy-400">No data.</p>
      ) : (
        <ul className="space-y-2">
          {items.map((r) => (
            <li key={r._id || "_"} className="text-xs">
              <div className="flex items-center justify-between mb-0.5">
                <span className="truncate">{r.label}</span>
                <span className="text-navy-500 font-mono">{r.count}</span>
              </div>
              <div className="h-1.5 bg-navy-100 rounded-full overflow-hidden">
                <div className="h-full bg-orange-400" style={{ width: `${(r.count / max) * 100}%` }} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
